import { useEffect, useMemo, useState } from 'react';
import { Icon, IconButton, LinearProgress, TablePagination, Paper, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow, Button, Typography, AlertColor } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { GrupoTesteService } from 'shared/services/grupoTeste/grupoTesteService';
import { CanaisService } from 'shared/services/canais/canaisService';
import { IListagemGrupoTeste, listKeyValueTipoGrupoTeste } from 'shared/types';
import { FerramentasDaListagem, IListaFiltros, AlertaSnackBar } from 'shared/components';
import { LayoutBaseDePagina } from 'shared/layouts';
import { useSelectedOrganization } from 'shared/store/organization';
import { getValueFromKeyValue, IKeyValue, listagemCanalToIKeyValue } from 'shared/utils';
import { useHasPermissionAcao } from 'shared/hooks';

export const ListagemDeGrupoTeste: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const navigate = useNavigate();
  const { t } = useTranslation();

  const [stateSnack, setStateSnack] = useState({
    open: false,
    severity: 'success',
    message: '',
  });

  const hasPermitionCreate = useHasPermissionAcao('grupoteste','CRE');
  const hasPermitionRead = useHasPermissionAcao('grupoteste','REA');
  
  const [rows, setRows] = useState<IListagemGrupoTeste[]>([]);

  const org = useSelectedOrganization();

  const [listKeyValueCanal, setListKeyValueCanal] = useState<IKeyValue[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const [idCanal, setIdCanal] = useState('');

  const [isLoadingCanal, setIsLoadingCanal] = useState(true);

  const [eventoPesquisar, setEventoPesquisar] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const buscaNome = useMemo(() => {
    return searchParams.get('nomeGrupoTeste') || '';
  }, [searchParams]);

  const buscaTipo = useMemo(() => {
    return searchParams.get('tipoGrupoTeste') || '';
  }, [searchParams]);

  const buscaOrderBy = useMemo(() => {
    return searchParams.get('orderBy') || '';
  }, [searchParams]);

  const offSet = useMemo(() => {
    return searchParams.get('offset') || '0';
  }, [searchParams]);
  
  const limit = useMemo(() => {
    return searchParams.get('limit') || '10';
  }, [searchParams]);

  const listaFiltros: IListaFiltros[] = [
    {
      tipo: 'Select',
      mapValue: listKeyValueCanal.map((itemk) => itemk),
      label: 'Canal',
      textoBusca: idCanal.toString(),
      handleTextoBusca: texto => { setIdCanal(texto.toString());}
    },
    {
      tipo: 'TextField',
      mapValue: [],
      label: 'Nome do Grupo',
      textoBusca: buscaNome,
      handleTextoBusca: texto => { searchParams.set('nomeGrupoTeste', texto.toString()); setSearchParams(searchParams, { replace: true }); }
    },
    {
      tipo: 'Select',
      mapValue: listKeyValueTipoGrupoTeste.map((itemk) => itemk),
      label: 'Tipo de grupo',
      textoBusca: buscaTipo,
      handleTextoBusca: texto => { searchParams.set('tipoGrupoTeste', texto.toString()); setSearchParams(searchParams, { replace: true }); }
    },
  ];

  const handleLimpar = () => {
    // eslint-disable-next-line prefer-const
    let parametros: string[] = [];
    searchParams.forEach(function (value, key) {
      parametros.push(key);
    });
    parametros.map((item) => (
      searchParams.delete(item)
    ));
    setIdCanal('');
    setPage(0);
    searchParams.set('offset', '0'); setSearchParams(searchParams, { replace: true });
    setEventoPesquisar(!eventoPesquisar);
  };

  const handlePesquisar = () => {
    setPage(0);
    searchParams.set('offset', '0'); setSearchParams(searchParams, { replace: true });
    setEventoPesquisar(!eventoPesquisar);
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
    searchParams.set('offset', (newPage * rowsPerPage).toString()); setSearchParams(searchParams, { replace: true });
    setEventoPesquisar(!eventoPesquisar);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    searchParams.set('limit', event.target.value.toString()); 
    searchParams.set('offset', '0');
    setSearchParams(searchParams, { replace: true });
    setEventoPesquisar(!eventoPesquisar);
  };

  const handleOrdenacao = (campo:string) => {
    searchParams.set('orderBy', campo); 
    setSearchParams(searchParams, { replace: true });
    setEventoPesquisar(!eventoPesquisar);
  };

  const handleCloseSnack = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setStateSnack({...stateSnack, open:false});
  };

  useEffect(() => {
    setIsLoadingCanal(true);

    CanaisService.getAll()
      .then((result) => {
        setIsLoadingCanal(false);
        if (result instanceof Error) {
          setStateSnack({open:true, severity:'error',message:`Ocorreu o seguinte erro: ${result.message}`});
        } else {
          setListKeyValueCanal(listagemCanalToIKeyValue(result));
        }
      });
  },[]);

  useEffect(() => {
    setIsLoading(true);
  
    GrupoTesteService.getAll(Number(org?.CodigoOrganizacao), idCanal==''?0:Number(idCanal) ,searchParams.toString())
      .then((result) => {
        setIsLoading(false);
        if (result instanceof Error) {
          setStateSnack({open:true, severity:'error',message:`Ocorreu o seguinte erro: ${result.message}`});
        } else {
          setTotalCount(result.count);
          setRows(result.rows);
          setRowsPerPage(Number.parseInt(limit.toString()));
          setPage(Number.parseInt(offSet.toString())/Number.parseInt(limit.toString()));
        }
      });

  },[eventoPesquisar]);

  return (
    <>
      <LayoutBaseDePagina
        titulo={t('pages.grupoTeste.titulo')}
        botaoLog={
          <Button
            color='info'
            disableElevation
            variant='contained'
            size='small'
            onClick={() => navigate('/grupoteste/listagem/logs')}
            startIcon={<Icon>crop</Icon>}
          >
            <Typography variant='button' whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
              {t('components.ferramentasDaListagem.logs')}
            </Typography>
          </Button>
        }
        drawerDeFiltros={
          <FerramentasDaListagem
            listaFiltros={listaFiltros}
            handlePesquisar={() => handlePesquisar()}
            handleLimpar={() => handleLimpar()}
          />
        }
      >
        <TableContainer component={Paper} variant="outlined" sx={{ m: 1, width: 'auto' }}>
          <Table stickyHeader >
            <TableHead>
              <TableRow>
                <TableCell width={60}>{t('global.acoes')}</TableCell>
                <TableCell onDoubleClick={() => handleOrdenacao(buscaOrderBy === 'NomeGrupoTeste_ASC'?'NomeGrupoTeste_DESC':'NomeGrupoTeste_ASC')}>
                  {t('pages.grupoTeste.nome')}
                  <Icon fontSize="inherit" sx={{verticalAlign:'middle'}}>{buscaOrderBy === 'NomeGrupoTeste_ASC'?'north':buscaOrderBy === 'NomeGrupoTeste_DESC'?'south':''}</Icon>
                </TableCell>
                <TableCell onDoubleClick={() => handleOrdenacao(buscaOrderBy === 'IdComCanal_ASC'?'IdComCanal_DESC':'IdComCanal_ASC')}>
                  {t('pages.grupoTeste.canal')}
                  <Icon fontSize="inherit" sx={{verticalAlign:'middle'}}>{buscaOrderBy === 'IdComCanal_ASC'?'north':buscaOrderBy === 'IdComCanal_DESC'?'south':''}</Icon>
                </TableCell>                
                <TableCell onDoubleClick={() => handleOrdenacao(buscaOrderBy === 'TipoGrupoTeste_ASC'?'TipoGrupoTeste_DESC':'TipoGrupoTeste_ASC')}>
                  {t('pages.grupoTeste.tipo')}
                  <Icon fontSize="inherit" sx={{verticalAlign:'middle'}}>{buscaOrderBy === 'TipoGrupoTeste_ASC'?'north':buscaOrderBy === 'TipoGrupoTeste_DESC'?'south':''}</Icon>
                </TableCell>    
              </TableRow>
            </TableHead>
            {!isLoading && (<TableBody>
              {rows.map((row) => (
                <TableRow key={row.IdGrupoTeste}>
                  <TableCell>
                    <IconButton size="small" disabled={!hasPermitionRead} onClick={() => navigate(`/grupoteste/detalhe/${row.IdComCanal}/${row.IdGrupoTeste}`)}>
                      <Icon>edit</Icon>
                    </IconButton>
                  </TableCell>
                  <TableCell>{row.NomeGrupoTeste}</TableCell>
                  <TableCell>{getValueFromKeyValue(listKeyValueCanal, row.IdComCanal.toString())}</TableCell>
                  <TableCell>{getValueFromKeyValue(listKeyValueTipoGrupoTeste, row.TipoGrupoTeste)}</TableCell>
                </TableRow>
              ))}
            </TableBody>)}

            {totalCount === 0 && !isLoading && (
              <caption>{t('global.listaVazia')}</caption>
            )}

            <TableFooter>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={10}>
                    <LinearProgress variant='indeterminate' />
                  </TableCell>
                </TableRow>
              )}
              {(!isLoading) && (
                <TableRow>
                  <TableCell colSpan={2}>
                    {hasPermitionCreate && (<Button
                      color='primary'
                      disableElevation
                      variant='outlined'
                      onClick={() => navigate('/grupoteste/detalhe/0/novo')}
                      startIcon={<Icon>add</Icon>}
                    >
                      <Typography variant='button' whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
                        {t('components.actions.novo')}
                      </Typography>
                    </Button>)}
                  </TableCell>
                  {(totalCount > 0 && totalCount > 10) && (<TableCell colSpan={8}>
                    <TablePagination
                      component="div"
                      labelDisplayedRows={({ from, to, count }) => `${from} - ${to} ${t('global.of')} ${count !== -1 ? count : `${t('global.moreThan')} ${to}`}`}
                      labelRowsPerPage={t('global.rowsPerPage')}
                      showFirstButton={true}
                      showLastButton={true}
                      page={page}
                      count={totalCount}
                      rowsPerPage={rowsPerPage}
                      rowsPerPageOptions={[5,8,10,20,50,100]}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    //onChange={(_, newPage) => setSearchParams({ busca, pagina: newPage.toString() }, { replace: true })}
                    />
                  </TableCell>
                  )}
                </TableRow>
              )}
            </TableFooter>
          </Table>
        </TableContainer>

      </LayoutBaseDePagina>
      <AlertaSnackBar 
        open={stateSnack.open} 
        severity={stateSnack.severity as AlertColor} 
        message={stateSnack.message} 
        handleClose={handleCloseSnack} 
      />
    </>
  );
};